<template>
  <a-modal
    :open="open"
    title="거래처 검색"
    :footer="null"
    width="720px"
    :destroy-on-close="true"
    @cancel="handleClose"
  >
    <div class="company-search">
      <!-- 검색 영역 -->
      <div class="search-section">
        <a-input-search
          v-model:value="searchKeyword"
          placeholder="거래처명 또는 사업자번호로 검색"
          enter-button="검색"
          size="large"
          :loading="isSearching"
          @search="handleSearch"
        />
      </div>

      <!-- 검색 결과 -->
      <div class="result-section">
        <div v-if="isLoading" class="loading-state">
          <a-spin size="large" tip="거래처 목록을 불러오는 중..." />
        </div>

        <div
          v-else-if="!hasSearched && allCompanies.length === 0"
          class="empty-state"
        >
          <a-empty description="등록된 거래처가 없습니다" />
        </div>

        <div v-else-if="isSearching" class="loading-state">
          <a-spin size="large" />
        </div>

        <div v-else-if="companies.length === 0" class="empty-state">
          <a-empty description="검색 결과가 없습니다" />
        </div>

        <div v-else class="company-list">
          <div
            v-for="company in companies"
            :key="company._id"
            class="company-item"
            :class="{ selected: selectedCompany?._id === company._id }"
            @click="selectCompany(company)"
          >
            <div class="company-info">
              <div class="company-header">
                <span class="company-name">{{ company.name }}</span>
                <a-tag
                  v-if="company.type"
                  :color="company.type === 'HOSPITAL' ? 'blue' : 'orange'"
                  size="small"
                >
                  {{ company.type === "HOSPITAL" ? "병원" : "제조사" }}
                </a-tag>
              </div>
              <div v-if="company.cNo" class="company-biz-number">
                {{ formatBusinessNumber(company.cNo) }}
              </div>
            </div>
            <div class="company-action">
              <a-button
                v-if="selectedCompany?._id === company._id"
                type="primary"
                size="small"
              >
                선택됨
              </a-button>
              <a-button v-else type="default" size="small"> 선택 </a-button>
            </div>
          </div>
        </div>
      </div>

      <!-- 하단 버튼 -->
      <div class="modal-footer">
        <a-button @click="handleClose">취소</a-button>
        <a-button
          type="primary"
          :disabled="!selectedCompany"
          @click="handleConfirm"
        >
          확인
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { defineComponent, ref, watch } from "vue";
import { resellerCustomerApi } from "../api";

export default defineComponent({
  name: "CompanySearchModal",
  props: {
    open: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:open", "select"],
  setup(props, { emit }) {
    const searchKeyword = ref("");
    const companies = ref([]);
    const allCompanies = ref([]);
    const selectedCompany = ref(null);
    const isSearching = ref(false);
    const hasSearched = ref(false);
    const isLoading = ref(false);

    // 거래처 목록 로드
    const loadCompanies = async () => {
      try {
        isLoading.value = true;
        const response = await resellerCustomerApi.getList({ limit: 100 });
        allCompanies.value = response.data.data.map((customer) => ({
          _id: customer._id,
          name: customer.name,
          type: customer.type,
          cNo: customer.cNo,
          addrOne: customer.addrOne,
          addrTwo: customer.addrTwo,
          addrThree: customer.addrThree,
        }));
        companies.value = [...allCompanies.value];
        hasSearched.value = true;
      } catch (error) {
        console.error("거래처 목록 로드 실패:", error);
        allCompanies.value = [];
        companies.value = [];
      } finally {
        isLoading.value = false;
      }
    };

    watch(
      () => props.open,
      (newVal) => {
        if (newVal) {
          searchKeyword.value = "";
          selectedCompany.value = null;
          hasSearched.value = false;
          loadCompanies();
        }
      }
    );

    const handleSearch = async () => {
      const keyword = searchKeyword.value.trim().toLowerCase();

      if (!keyword) {
        companies.value = [...allCompanies.value];
        return;
      }

      isSearching.value = true;
      hasSearched.value = true;

      companies.value = allCompanies.value.filter((company) => {
        const nameMatch = company.name?.toLowerCase().includes(keyword);
        const cNoMatch = company.cNo?.includes(keyword);
        return nameMatch || cNoMatch;
      });

      isSearching.value = false;
    };

    const selectCompany = (company) => {
      selectedCompany.value = company;
    };

    const formatBusinessNumber = (num) => {
      if (!num || num.length !== 10) return num;
      return `${num.slice(0, 3)}-${num.slice(3, 5)}-${num.slice(5)}`;
    };

    const handleClose = () => {
      emit("update:open", false);
    };

    const handleConfirm = () => {
      if (selectedCompany.value) {
        emit("select", selectedCompany.value);
        emit("update:open", false);
      }
    };

    return {
      searchKeyword,
      companies,
      allCompanies,
      selectedCompany,
      isSearching,
      isLoading,
      hasSearched,
      handleSearch,
      selectCompany,
      formatBusinessNumber,
      handleClose,
      handleConfirm,
    };
  },
});
</script>

<style scoped>
.company-search {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.search-section {
  padding: 8px 0;
}

.result-section {
  min-height: 300px;
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 16px;
}

.empty-state,
.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
}

.company-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.company-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #fafafa;
  border-radius: 8px;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s;
}

.company-item:hover {
  background: #f0f5ff;
  border-color: #d6e4ff;
}

.company-item.selected {
  background: #e6f7ff;
  border-color: #1890ff;
}

.company-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.company-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.company-name {
  font-size: 16px;
  font-weight: 600;
  color: #262626;
}

.company-biz-number {
  font-size: 13px;
  color: #8c8c8c;
}

.company-action {
  flex-shrink: 0;
  margin-left: 16px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}
</style>
